import { AppSelect } from './ui/AppSelect';
import React, { useState, useEffect, useRef } from 'react';
import { Wand2, Sparkles, Layers, Plus, Loader2, Upload, X, Palette, Trash2, Edit2, Check, Tag, RefreshCw, ImagePlus, LayoutGrid, Camera, PackagePlus, ChevronRight, Download } from 'lucide-react';
import { authenticatedFetch } from '../utils/api';
import { API_HOST } from '../utils/constants';
import { runImageTaskToUrl } from '../services/taskService';
import { STYLE_OPTIONS } from "./storyboard/common/Constants";
import { useTheme } from "../context/ThemeContext";
import { useStyle } from "../context/StyleContext";
import { useToast } from "../context/ToastContext";
import { ConfirmDialog } from '@ui';
import CameraController from './scene/CameraController';
import { mapCameraToPrompt, mapCameraToLabel } from '../utils/CameraAngleMapper';
import AssetRenameDialog from "./assets/AssetRenameDialog";
import WorkshopVariantUpload from './assets/WorkshopVariantUpload';
import WorkshopArtwork from "./ui/WorkshopArtwork";
import "./assets/WorkshopShell.css";
import { ModelArkImageLibraryStatus, ModelArkLibraryToolbar } from './assets/ModelArkLibraryControls';
import { useModelArkAssets } from '../hooks/useModelArkAssets';
import { collectWorkshopModelArkReferences } from '../utils/modelArkAssets';
import { applyAssetCreateResult, applyAssetStatusResult, assetLibraryLabel, assetStatusLabel, clearVideoAssetBinding, getEffectiveAssetMeta, getVideoAssetFieldNames, isAssetReadyStatus } from "../utils/guaVideoAssetStatus";
import { getProjectVideoAssetApiBase, getProjectVideoProvider, supportsVideoAssetIngest } from "../utils/projectRuntime";
import { useWorkshopImageSettings } from "../hooks/useWorkshopImageSettings";
import { createWorkshopAssetSaveQueue, mergeWorkshopAssetRegistration, uploadWorkshopVariant, validateVariantImageFile } from '../utils/workshopVariantUpload';
import {
  WANX_IMAGE_MAX_INPUT_IMAGES,
  assertWanxImageInputLimit,
  isWanxImageProvider,
  normalizeWanxImageQualityTier,
} from "../utils/wanxImageCapabilities";
import {
  isLatestWorkshopListRequest,
  isSameWorkshopAssetId,
  mergeWorkshopAssetById,
  nextWorkshopListRequestSequence,
} from "../utils/workshopAssetState";

const SceneGenerator = ({ projectId }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const { currentStyle, setCurrentStyle } = useStyle();
  const toast = useToast();
  const assetProvider = getProjectVideoProvider(projectId);
  const assetApiBase = getProjectVideoAssetApiBase(projectId);
  const assetIngestSupported = supportsVideoAssetIngest(assetProvider);
  const { imageProvider, imageQualityTier, availableImageQualityTierOptions, handleImageQualityTierChange } = useWorkshopImageSettings();
  const isWanxImage = isWanxImageProvider(imageProvider);
  const maxReferenceImages = isWanxImage ? WANX_IMAGE_MAX_INPUT_IMAGES : 5;

  const runWorkshopImageTaskToUrl = (taskType, payload, options = {}) => {
    const imageInputs = [
      payload?.baseImage,
      payload?.baseImageUrl,
      payload?.refImageUrl,
      ...(Array.isArray(payload?.referenceImages) ? payload.referenceImages : []),
    ].filter(Boolean);
    assertWanxImageInputLimit(imageInputs, imageProvider);
    return runImageTaskToUrl(taskType, {
      ...payload,
      imageQualityTier: normalizeWanxImageQualityTier(payload?.imageQualityTier, imageProvider),
    }, {
      ...options,
      provider: imageProvider,
    });
  };

  // 场景列表和选中状态
  const [scenes, setScenes] = useState([]);
  const modelArkRegistry = useModelArkAssets({ projectId, projectProvider: assetProvider, references: collectWorkshopModelArkReferences(scenes) });
  const [selectedSceneId, setSelectedSceneId] = useState(null);

  // 新建场景弹窗
  const [isCreating, setIsCreating] = useState(false);
  const [newSceneName, setNewSceneName] = useState("");
  const [newScenePrompt, setNewScenePrompt] = useState("");
  const [newSceneAliases, setNewSceneAliases] = useState("");
  const [newSceneMode, setNewSceneMode] = useState("single"); // "single" | "multiAngle"
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [referenceImages, setReferenceImages] = useState([null, null, null, null, null]);
  const [uploadingRefIndex, setUploadingRefIndex] = useState(null);

  // 别名编辑
  const [editingAlias, setEditingAlias] = useState(false);
  const [editAliasText, setEditAliasText] = useState("");

  // 描述编辑
  const [editingDescription, setEditingDescription] = useState(false);
  const [editDescriptionText, setEditDescriptionText] = useState("");

  // 按场景 id 追踪 loading 状态，避免切换场景时状态错位（使用 Set 支持多资源并发）
  const [regeneratingMainIds, setRegeneratingMainIds] = useState(new Set());
  const [replacingMainIds, setReplacingMainIds] = useState(new Set());
  const [generatingVariantIds, setGeneratingVariantIds] = useState(new Set());
  const [regeneratingVariantKeys, setRegeneratingVariantKeys] = useState(new Set()); // "sceneId-variantIndex"
  const [isSplittingVariants, setIsSplittingVariants] = useState(false);
  const [refiningIds, setRefiningIds] = useState(new Set());
  const [uploadingAssetIds, setUploadingAssetIds] = useState(new Set());

  // 变体生成
  const [showVariantPanel, setShowVariantPanel] = useState(false);
  const [variantName, setVariantName] = useState("");
  const [variantPrompt, setVariantPrompt] = useState("");
  const [deleteVariantIndex, setDeleteVariantIndex] = useState(null);

  // 变体展开（手风琴）
  const [expandedVariantIndex, setExpandedVariantIndex] = useState(null);
  const [variantRefinePrompt, setVariantRefinePrompt] = useState("");
  const [showVariantRefinePanel, setShowVariantRefinePanel] = useState(false);
  const [editingVariantDesc, setEditingVariantDesc] = useState(false);
  const [editVariantDescText, setEditVariantDescText] = useState("");
  const variantReplaceInputRefs = useRef(new Map());

  // 自动高度 textarea refs
  const refinePromptTextareaRef = useRef(null);
  const editDescriptionTextareaRef = useRef(null);
  const variantPromptTextareaRef = useRef(null);
  const variantRefinePromptTextareaRef = useRef(null);
  const editVariantDescTextareaRef = useRef(null);

  // 摄像机控制器
  const [showCameraPanel, setShowCameraPanel] = useState(false);
  const [cameraAzimuth, setCameraAzimuth] = useState(0);
  const [cameraElevation, setCameraElevation] = useState(0);
  const [cameraDistance, setCameraDistance] = useState(1.5);
  const [isGeneratingCameraVariant, setIsGeneratingCameraVariant] = useState(false);

  // 微调模式
  const [showRefinePanel, setShowRefinePanel] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState("");

  // 详情页参考图编辑
  const [editingDetailRefs, setEditingDetailRefs] = useState(false);
  const [detailRefImages, setDetailRefImages] = useState([null, null, null, null, null]);
  const [uploadingDetailRefIndex, setUploadingDetailRefIndex] = useState(null);
  const detailRefInputRefs = useRef([]);

  // 其他
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const replaceMainInputRef = useRef(null);
  const refImageInputRefs = useRef([]);
  const pollingIdsRef = useRef(new Map());
  const pollingTimersRef = useRef(new Map());
  const scenesRef = useRef([]);
  const assetSaveQueueRef = useRef(null);
  if (!assetSaveQueueRef.current) assetSaveQueueRef.current = createWorkshopAssetSaveQueue();
  const currentProjectIdRef = useRef(projectId);
  currentProjectIdRef.current = projectId;
  const sceneListRequestSequenceRef = useRef(0);
  const API_BASE = API_HOST;
  const createLocalToken = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  const stopAssetPolling = (entityId) => {
    const timeoutId = pollingTimersRef.current.get(entityId);
    if (timeoutId != null) window.clearTimeout(timeoutId);
    pollingTimersRef.current.delete(entityId);
    pollingIdsRef.current.delete(entityId);
  };

  const scheduleAssetPoll = (entityId, token, poll, delayMs) => {
    if (pollingIdsRef.current.get(entityId) !== token) return;
    const previousTimeoutId = pollingTimersRef.current.get(entityId);
    if (previousTimeoutId != null) window.clearTimeout(previousTimeoutId);
    const timeoutId = window.setTimeout(() => {
      pollingTimersRef.current.delete(entityId);
      if (pollingIdsRef.current.get(entityId) === token) poll();
    }, delayMs);
    pollingTimersRef.current.set(entityId, timeoutId);
  };

  const stopAllAssetPolling = () => {
    pollingTimersRef.current.forEach(timeoutId => window.clearTimeout(timeoutId));
    pollingTimersRef.current.clear();
    pollingIdsRef.current.clear();
  };

  const saveSceneAsset = async (asset, { shouldApply = () => true, rebase } = {}) => {
    return assetSaveQueueRef.current.run(asset.id ?? "new", async () => {
      const latest = getSceneById(asset.id);
      if (!shouldApply()) return latest;
      if (rebase && !latest) throw new Error("原场景已删除或已切换项目，未保存");
      const payload = rebase ? rebase(latest) : asset;
      const saved = await authenticatedFetch(`${API_BASE}/api/scenes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (saved?.id != null && shouldApply()) {
        sceneListRequestSequenceRef.current = nextWorkshopListRequestSequence(sceneListRequestSequenceRef.current);
        scenesRef.current = mergeWorkshopAssetById(scenesRef.current, saved);
        setScenes(prev => mergeWorkshopAssetById(prev, saved));
      }
      return saved;
    });
  };

  const getSceneById = (id) => scenesRef.current.find(scene => String(scene.id) === String(id));

  useEffect(() => {
    if (projectId) fetchScenes();
  }, [projectId]);

  useEffect(() => () => {
    stopAllAssetPolling();
    sceneListRequestSequenceRef.current = nextWorkshopListRequestSequence(sceneListRequestSequenceRef.current);
  }, [projectId, assetProvider]);

  useEffect(() => { scenesRef.current = scenes; }, [scenes]);

  // 自动调整 textarea 高度
  useEffect(() => {
    const adjustHeight = (ref) => {
      if (ref.current) {
        ref.current.style.height = 'auto';
        ref.current.style.height = ref.current.scrollHeight + 'px';
      }
    };
    if (showRefinePanel) adjustHeight(refinePromptTextareaRef);
    if (editingDescription) adjustHeight(editDescriptionTextareaRef);
    if (showVariantPanel) adjustHeight(variantPromptTextareaRef);
    if (showVariantRefinePanel) adjustHeight(variantRefinePromptTextareaRef);
    if (editingVariantDesc) adjustHeight(editVariantDescTextareaRef);
  }, [showRefinePanel, editingDescription, showVariantPanel, showVariantRefinePanel, editingVariantDesc, refinePrompt, editDescriptionText, variantPrompt, variantRefinePrompt, editVariantDescText]);

  const fetchScenes = async ({ throwOnError = false } = {}) => {
    const requestSequence = nextWorkshopListRequestSequence(sceneListRequestSequenceRef.current);
    sceneListRequestSequenceRef.current = requestSequence;
    try {
      const data = await authenticatedFetch(`${API_BASE}/api/scenes?projectId=${projectId}`);
      const list = data || [];
      if (!isLatestWorkshopListRequest(requestSequence, sceneListRequestSequenceRef.current)) {
        return { applied: false, stale: true, list };
      }
      scenesRef.current = list;
      setScenes(list);
      if (list.length > 0) setSelectedSceneId(currentId => currentId ?? list[0].id);
      list.forEach(s => {
        const hasProcessing = getEffectiveAssetMeta(s, assetProvider).status === "Processing"
          || (s.variations || []).some(v => getEffectiveAssetMeta(v, assetProvider).status === "Processing");
        if (hasProcessing) pollAssetStatus(s);
      });
      return { applied: true, stale: false, list };
    } catch (e) {
      if (isLatestWorkshopListRequest(requestSequence, sceneListRequestSequenceRef.current)) {
        console.error(e);
      }
      if (throwOnError) throw e;
      return { applied: false, stale: false, error: e };
    }
  };

  const recoverSceneImageAfterTaskError = async (sceneId, previousImageUrl = null) => {
    const refreshed = await fetchScenes();
    if (!refreshed?.applied) return false;
    const asset = refreshed.list.find(item => isSameWorkshopAssetId(item.id, sceneId));
    const currentUrl = String(asset?.imageUrl || "").trim();
    return Boolean(currentUrl && currentUrl !== String(previousImageUrl || "").trim());
  };

  // 上传参考图（新建弹窗用）
  const handleUploadRefImage = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!referenceImages[index] && referenceImages.filter(Boolean).length >= maxReferenceImages) {
      toast.warning(`当前图片模型最多支持${maxReferenceImages}张参考图`);
      e.target.value = "";
      return;
    }
    setUploadingRefIndex(index);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      setReferenceImages(prev => {
        const next = [...prev];
        next[index] = imageUrl;
        return next;
      });
    } catch (err) {
      toast.error("上传失败: " + err.message);
    } finally {
      setUploadingRefIndex(null);
      e.target.value = "";
    }
  };

  // 删除参考图（新建弹窗）
  const handleRemoveRefImage = (index) => {
    setReferenceImages(prev => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  // 详情页参考图上传
  const handleUploadDetailRefImage = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!detailRefImages[index] && detailRefImages.filter(Boolean).length >= maxReferenceImages) {
      toast.warning(`当前图片模型最多支持${maxReferenceImages}张参考图`);
      e.target.value = "";
      return;
    }
    setUploadingDetailRefIndex(index);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      setDetailRefImages(prev => {
        const next = [...prev];
        next[index] = imageUrl;
        return next;
      });
    } catch (err) {
      toast.error("上传失败: " + err.message);
    } finally {
      setUploadingDetailRefIndex(null);
      e.target.value = "";
    }
  };

  // 删除详情页参考图
  const handleRemoveDetailRefImage = (index) => {
    setDetailRefImages(prev => {
      const next = [...prev];
      next[index] = null;
      return next;
    });
  };

  // 开始编辑详情页参考图
  const handleStartEditDetailRefs = () => {
    const savedRefs = (() => { try { return JSON.parse(selectedScene.referenceImages || '[]'); } catch { return []; } })();
    const refs = [null, null, null, null, null];
    savedRefs.forEach((url, i) => { if (i < 5) refs[i] = url; });
    setDetailRefImages(refs);
    setEditingDetailRefs(true);
  };

  // 保存详情页参考图
  const handleSaveDetailRefs = async () => {
    if (!selectedScene) return;
    try {
      const refUrls = detailRefImages.filter(Boolean);
      await saveSceneAsset({ ...selectedScene, referenceImages: refUrls.length > 0 ? JSON.stringify(refUrls) : null });
      await fetchScenes();
      setEditingDetailRefs(false);
      toast.success("参考图已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  const selectedScene = scenes.find(s => isSameWorkshopAssetId(s.id, selectedSceneId));
  const sceneAliases = selectedScene ? (() => { try { return JSON.parse(selectedScene.aliases || '[]'); } catch { return []; } })() : [];
  const sceneVariations = selectedScene?.variations || [];

  // 派生当前场景的 loading 状态
  const isRegeneratingMain = regeneratingMainIds.has(selectedSceneId);
  const isReplacingMain = replacingMainIds.has(selectedSceneId);
  const isGeneratingVariant = generatingVariantIds.has(selectedSceneId);
  const isRefiningMain = refiningIds.has(selectedSceneId);
  const getRegeneratingVariantIndex = (idx) => regeneratingVariantKeys.has(`${selectedSceneId}-${idx}`);

  // 校验新建场景表单
  const validateNewSceneForm = () => {
    if (!newSceneName.trim()) {
      toast.warning("请填写场景名称");
      return false;
    }
    if (!newSceneAliases.trim()) {
      toast.warning("请填写场景别名");
      return false;
    }
    if (!newScenePrompt.trim()) {
      toast.warning("请填写场景描述");
      return false;
    }
    return true;
  };

  // 创建无图场景
  const createSceneDraft = async () => {
    const aliasArray = newSceneAliases.split(/[,，]/).map(s => s.trim()).filter(Boolean);
    const refUrls = referenceImages.filter(Boolean);
    return await saveSceneAsset({
      projectId,
      name: newSceneName,
      description: newScenePrompt,
      imageUrl: null,
      aliases: JSON.stringify(aliasArray),
      sceneMode: newSceneMode,
      referenceImages: refUrls.length > 0 ? JSON.stringify(refUrls) : null,
      variations: []
    });
  };

  // 新建场景 - AI生成并创建
  const handleGenerateNewMain = async () => {
    if (!validateNewSceneForm()) return;
    if (newSceneMode !== "multiAngle") {
      try {
        assertWanxImageInputLimit(referenceImages, imageProvider);
      } catch (error) {
        toast.warning(error.message);
        return;
      }
    }
    
    // 先创建无图场景
    setIsGenerating(true);
    let newScene;
    try {
      newScene = await createSceneDraft();
      await fetchScenes();
      setSelectedSceneId(newScene.id);
      setIsCreating(false); // 关闭弹窗
    } catch (e) {
      toast.error("创建场景失败: " + e.message);
      setIsGenerating(false);
      return;
    }
    
    // 开始异步生成（复用重新生成的逻辑）
    const sceneId = newScene.id;
    const prompt = newScenePrompt;
    setRegeneratingMainIds(prev => new Set(prev).add(sceneId));
    
    try {
      const refUrls = referenceImages.filter(Boolean);
      let newImageUrl;
      if (newSceneMode === "multiAngle") {
        newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_MULTI_ANGLE_SCENE", {
          sceneDescription: prompt,
          name: newScene.name,
          projectId,
          imageQualityTier,
          workshopAssetType: "scene",
          workshopAssetId: sceneId,
          workshopTarget: "main",
        }, { styleAnchor: currentStyle, projectId, source: "scene_multi_angle" });
      } else {
        newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_SCENE", {
          prompt,
          name: newScene.name,
          projectId,
          isCutaway: false,
          imageQualityTier,
          referenceImages: refUrls.length > 0 ? refUrls : undefined,
          workshopAssetType: "scene",
          workshopAssetId: sceneId,
          workshopTarget: "main",
        }, { styleAnchor: currentStyle, projectId, source: "scene_workshop" });
      }
      const latest = getSceneById(sceneId) || newScene;
      await saveSceneAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchScenes();
      // 清空表单
      setNewSceneName(""); setNewScenePrompt(""); setNewSceneAliases(""); setNewSceneMode("single");
      setReferenceImages([null, null, null, null, null]);
      toast.success("场景生成成功！");
    } catch (e) {
      const recovered = await recoverSceneImageAfterTaskError(sceneId);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("生成失败: " + e.message);
    } finally {
      setRegeneratingMainIds(prev => {
        const next = new Set(prev);
        next.delete(sceneId);
        return next;
      });
      setIsGenerating(false);
    }
  };

  // 新建场景 - 上传主图并创建
  const handleUploadNew = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // 先校验表单
    if (!validateNewSceneForm()) {
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    setIsUploading(true);
    
    // 上传图片
    const formData = new FormData();
    formData.append('file', file);
    let imageUrl;
    try {
      imageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: 'POST', body: formData });
    } catch (err) { 
      toast.error("上传失败: " + err.message);
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    
    // 创建有图场景
    try {
      const aliasArray = newSceneAliases.split(/[,，]/).map(s => s.trim()).filter(Boolean);
      const refUrls = referenceImages.filter(Boolean);
      const saved = await saveSceneAsset({
        projectId,
        name: newSceneName,
        description: newScenePrompt,
        imageUrl: imageUrl,
        aliases: JSON.stringify(aliasArray),
        sceneMode: newSceneMode,
        referenceImages: refUrls.length > 0 ? JSON.stringify(refUrls) : null,
        variations: []
      });
      await fetchScenes();
      setSelectedSceneId(saved?.id || null);
      setIsCreating(false);
      // 清空表单
      setNewSceneName(""); setNewScenePrompt(""); setNewSceneAliases(""); setNewSceneMode("single");
      setReferenceImages([null, null, null, null, null]);
      toast.success("场景已创建！");
    } catch (e) {
      toast.error("保存失败: " + e.message);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };



  // 一键生成：为所有有描述但没图片的场景批量生图
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [batchProgress, setBatchProgress] = useState({ done: 0, total: 0 });

  const handleBatchGenerate = async () => {
    const pending = scenes.filter(s => s.description && s.description !== "场景图" && !s.imageUrl);
    if (pending.length === 0) return toast.info("所有场景都已有图片，无需生成");
    setBatchGenerating(true);
    setBatchProgress({ done: 0, total: pending.length });
    let successCount = 0;
    for (const scene of pending) {
      try {
        setRegeneratingMainIds(prev => new Set(prev).add(scene.id));
        const sceneRefImages = (() => { try { return JSON.parse(scene.referenceImages || '[]'); } catch { return []; } })();
        let newImageUrl;
        if (scene.sceneMode === 'multiAngle') {
          newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_MULTI_ANGLE_SCENE", {
            sceneDescription: scene.description,
            name: scene.name,
            projectId,
            imageQualityTier,
            workshopAssetType: "scene",
            workshopAssetId: scene.id,
            workshopTarget: "main",
          }, { styleAnchor: currentStyle, projectId, source: "scene_multi_angle" });
        } else {
          newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_SCENE", {
            prompt: scene.description,
            name: scene.name,
            projectId,
            isCutaway: false,
            imageQualityTier,
            referenceImages: sceneRefImages.length > 0 ? sceneRefImages : undefined,
            workshopAssetType: "scene",
            workshopAssetId: scene.id,
            workshopTarget: "main",
          }, { styleAnchor: currentStyle, projectId, source: "scene_workshop" });
        }
        const latest = getSceneById(scene.id) || scene;
        await saveSceneAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
        successCount++;
      } catch (e) {
        const recovered = await recoverSceneImageAfterTaskError(scene.id, scene.imageUrl);
        if (recovered) {
          successCount++;
        } else {
          console.error(`[BatchGen] 场景 ${scene.name} 生成失败:`, e.message);
        }
        if (!recovered && isWanxImage && String(e.message || "").includes("千问图像3.0最多支持")) {
          toast.error(`${scene.name}：${e.message}`);
        }
      } finally {
        setRegeneratingMainIds(prev => { const next = new Set(prev); next.delete(scene.id); return next; });
        setBatchProgress(prev => ({ ...prev, done: prev.done + 1 }));
      }
    }
    await fetchScenes();
    setBatchGenerating(false);
    toast.success(`一键生成完成：${successCount}/${pending.length} 个场景`);
  };

  // 主体图重新生成（根据 sceneMode 选择接口）
  const handleRegenerateMain = async () => {
    if (!selectedScene) return;
    const sceneId = selectedScene.id;
    const prompt = selectedScene.description;
    if (!prompt || prompt === "场景图") return toast.warning("该场景没有描述，无法重新生成");
    setRegeneratingMainIds(prev => new Set(prev).add(sceneId));
    try {
      const sceneRefImages = (() => { try { return JSON.parse(selectedScene.referenceImages || '[]'); } catch { return []; } })();
      let newImageUrl;
      if (selectedScene.sceneMode === 'multiAngle') {
        newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_MULTI_ANGLE_SCENE", {
          sceneDescription: prompt,
          name: selectedScene.name,
          projectId,
          imageQualityTier,
          workshopAssetType: "scene",
          workshopAssetId: sceneId,
          workshopTarget: "main",
        }, { styleAnchor: currentStyle, projectId, source: "scene_multi_angle" });
      } else {
        newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_SCENE", {
          prompt,
          name: selectedScene.name,
          projectId,
          isCutaway: false,
          imageQualityTier,
          referenceImages: sceneRefImages.length > 0 ? sceneRefImages : undefined,
          workshopAssetType: "scene",
          workshopAssetId: sceneId,
          workshopTarget: "main",
        }, { styleAnchor: currentStyle, projectId, source: "scene_workshop" });
      }
      stopAssetPolling(sceneId);
      const latest = getSceneById(sceneId) || selectedScene;
      await saveSceneAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchScenes();
      toast.success("重新生成成功！");
    } catch (e) {
      const recovered = await recoverSceneImageAfterTaskError(sceneId, selectedScene.imageUrl);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("生成失败: " + e.message);
    }
    finally {
      setRegeneratingMainIds(prev => {
        const next = new Set(prev);
        next.delete(sceneId);
        return next;
      });
    }
  };

  // 主体图微调（基于当前图 + 修改描述）
  const handleRefineMain = async () => {
    if (!selectedScene || !selectedScene.imageUrl) return;
    if (!refinePrompt.trim()) return toast.warning("请填写修改描述");
    const sceneId = selectedScene.id;
    setRefiningIds(prev => new Set(prev).add(sceneId));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_SCENE_VARIANT", {
        originalPrompt: selectedScene.description || "",
        baseImageUrl: selectedScene.imageUrl,
        variantPrompt: refinePrompt,
        name: selectedScene.name,
        projectId,
        imageQualityTier,
        workshopAssetType: "scene",
        workshopAssetId: sceneId,
        workshopTarget: "main",
      }, { styleAnchor: currentStyle, projectId, source: "scene_variant" });
      stopAssetPolling(sceneId);
      const latest = getSceneById(sceneId) || selectedScene;
      await saveSceneAsset({ ...clearVideoAssetBinding(latest), imageUrl: newImageUrl });
      await fetchScenes();
      setShowRefinePanel(false);
      setRefinePrompt("");
      toast.success("微调完成！");
    } catch (e) {
      const recovered = await recoverSceneImageAfterTaskError(sceneId, selectedScene.imageUrl);
      if (recovered) toast.warning("图片已生成，前端回执异常，已自动同步");
      else toast.error("微调失败: " + e.message);
    }
    finally {
      setRefiningIds(prev => {
        const next = new Set(prev);
        next.delete(sceneId);
        return next;
      });
    }
  };

  // 主体图上传替换
  const handleReplaceMainImage = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedScene) return;
    const sceneId = selectedScene.id;
    setReplacingMainIds(prev => new Set(prev).add(sceneId));
    const formData = new FormData();
    formData.append("file", file);
    try {
      const newImageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      stopAssetPolling(sceneId);
      await saveSceneAsset({ ...clearVideoAssetBinding(selectedScene), imageUrl: newImageUrl });
      await fetchScenes();
      toast.success("图片已替换");
    } catch (err) { toast.error("上传失败: " + err.message); }
    finally {
      setReplacingMainIds(prev => {
        const next = new Set(prev);
        next.delete(sceneId);
        return next;
      });
      if (replaceMainInputRef.current) replaceMainInputRef.current.value = "";
    }
  };

  // 删除变体
  const handleDeleteVariant = async (idx) => {
    if (!selectedScene) return;
    const updatedVariations = sceneVariations.filter((_, i) => i !== idx);
    try {
      await saveSceneAsset({ ...selectedScene, variations: updatedVariations });
      await fetchScenes();
      toast.success(sceneVariations[idx]?.imageUrl ? "变体已移入回收站，可联系管理员恢复" : "未生成图片的变体草稿已移除");
    } catch (e) { toast.error("删除失败: " + e.message); }
    finally { setDeleteVariantIndex(null); }
  };

  // 重新生成变体
  const handleRegenerateVariant = async (idx) => {
    const variant = sceneVariations[idx];
    if (!selectedScene || !variant) return;
    if (!variant.description && !variant.title) return toast.warning("该变体没有描述，无法重新生成");
    const sceneId = selectedScene.id;
    const variantKey = `${sceneId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    try {
      const variantDesc = variant.description || variant.title;
      const basePrompt = selectedScene.description || "";
      // 使用 generateVariation 接口，传递 baseImageUrl 作为参考图
      // 从变体的 description 中提取 variationKey（如果有的话）
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_VARIATION", {
        baseImage: selectedScene.imageUrl,
        extraPrompt: variantDesc,
        originalPrompt: basePrompt,
        variationKey: "medium_shot",
        name: variant.title || selectedScene.name,
        projectId,
        imageQualityTier,
      }, { styleAnchor: currentStyle, projectId, source: "scene_variation" });
      const updatedVariations = sceneVariations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(sceneId);
      await saveSceneAsset({ ...selectedScene, variations: updatedVariations });
      await fetchScenes();
      toast.success("变体已重新生成！");
    } catch (e) { toast.error("生成失败: " + e.message); }
    finally {
      setRegeneratingVariantKeys(prev => {
        const next = new Set(prev);
        next.delete(variantKey);
        return next;
      });
    }
  };

  // 裁切16宫格为16个变体（纯裁切，不生图）
  const ANGLE_NAMES = [
    "远景·全貌", "俯拍·布局", "斜俯·纵深", "侧面·剖面",
    "中景·正面", "中景·反打", "中景·侧面", "中景·微仰",
    "特写·质感", "特写·氛围", "仰拍·高度", "仰拍·结构",
    "回望·入口", "遮挡·窥视", "侧后·旁观", "剪影·氛围"
  ];
  const handleSplitToVariants = async () => {
    if (!selectedScene?.imageUrl) return toast.warning("需要主图才能裁切");
    setIsSplittingVariants(true);
    try {
      const splitResult = await authenticatedFetch(`${API_BASE}/api/video/split-image-only`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ gridImageUrl: selectedScene.imageUrl, rows: 4, cols: 4 }),
      });
      const splitImages = splitResult.images || [];
      const newVariations = splitImages.map((url, i) => ({
        title: ANGLE_NAMES[i] || `角度${i + 1}`,
        imageUrl: url,
        description: "多角度自动裁切",
      }));
      const updatedVariations = [...sceneVariations, ...newVariations];
      await saveSceneAsset({ ...selectedScene, variations: updatedVariations });
      await fetchScenes();
      toast.success("裁切完成！已添加 " + splitImages.length + " 个变体");
    } catch (e) { toast.error("裁切失败: " + e.message); } finally { setIsSplittingVariants(false); }
  };

  // 生成变体
  const handleGenerateVariant = async () => {
    if (!selectedScene) return;
    if (!variantName.trim()) return toast.warning("请填写变体名称");
    if (!variantPrompt.trim()) return toast.warning("请填写变体描述");
    const sceneId = selectedScene.id;
    setGeneratingVariantIds(prev => new Set(prev).add(sceneId));
    try {
      const basePrompt = selectedScene.description || "";
      // 使用 generateVariation 接口，传递 baseImageUrl 作为参考图
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_VARIATION", {
        baseImage: selectedScene.imageUrl,
        extraPrompt: variantPrompt,
        originalPrompt: basePrompt,
        variationKey: "medium_shot",
        name: variantName.trim(),
        projectId,
        imageQualityTier,
      }, { styleAnchor: currentStyle, projectId, source: "scene_variation" });
      const updatedVariations = [...sceneVariations, { title: variantName.trim(), imageUrl: newImageUrl, description: variantPrompt }];
      await saveSceneAsset({ ...selectedScene, variations: updatedVariations });
      await fetchScenes();
      setShowVariantPanel(false);
      setVariantName(""); setVariantPrompt("");
      toast.success("变体生成成功！");
    } catch (e) { toast.error("生成失败: " + e.message); }
    finally {
      setGeneratingVariantIds(prev => {
        const next = new Set(prev);
        next.delete(sceneId);
        return next;
      });
    }
  };

  // 摄像机角度生成变体
  const handleGenerateCameraVariant = async () => {
    if (!selectedScene) return;
    if (!selectedScene.imageUrl) return toast.warning("当前场景没有图片，无法生成摄像机变体");
    setIsGeneratingCameraVariant(true);
    try {
      const cameraPromptText = mapCameraToPrompt(cameraAzimuth, cameraElevation, cameraDistance);
      const label = mapCameraToLabel(cameraAzimuth, cameraElevation, cameraDistance);
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_SCENE_WITH_CAMERA", {
        sceneDescription: selectedScene.description || "",
        cameraPrompt: cameraPromptText,
        baseImage: selectedScene.imageUrl,
        name: label.fullText,
        projectId,
        imageQualityTier,
      }, { styleAnchor: currentStyle, projectId, source: "scene_camera" });
      const updatedVariations = [...sceneVariations, {
        title: label.fullText,
        imageUrl: newImageUrl,
        description: "摄像机: " + label.fullText,
      }];
      await saveSceneAsset({ ...selectedScene, variations: updatedVariations });
      await fetchScenes();
      setShowCameraPanel(false);
      toast.success("摄像机变体生成成功！");
    } catch (e) { toast.error("生成失败: " + e.message); } finally { setIsGeneratingCameraVariant(false); }
  };

  // 上传素材到 GuaVideo 素材库（主体图 + 所有变体），保存 assetId 和状态
  const handleUploadToAssets = async () => {
    if (!assetIngestSupported) return toast.info("当前项目视频服务不支持素材入库");
    if (!selectedScene?.imageUrl) return toast.warning("没有主体图，无法上传素材库");
    const sceneId = selectedScene.id;
    setUploadingAssetIds(prev => new Set(prev).add(sceneId));
    try {
      const updated = { ...selectedScene };
      try {
        const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/create`, {
          method: "POST",
          body: JSON.stringify({ projectId, url: updated.imageUrl, name: updated.name || "场景主体", assetType: "Image", typeCode: "scene_image", source: "asset", sourceAssetId: sceneId, sourceDetail: "scene_generator_main" }),
        });
        const applied = applyAssetCreateResult(updated, res, assetProvider);
        if (applied.accepted) {
          Object.assign(updated, applied.target);
        }
      } catch (e) { toast.error("主体图入库失败: " + e.message); }

      const variations = [...(updated.variations || [])];
      for (let i = 0; i < variations.length; i++) {
        const v = variations[i];
        if (!v.imageUrl) continue;
        try {
          const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/create`, {
            method: "POST",
            body: JSON.stringify({ projectId, url: v.imageUrl, name: `${updated.name || "场景"}-${v.title || `变体${i + 1}`}`, assetType: "Image", typeCode: "scene_image", source: "asset", sourceAssetId: sceneId, sourceDetail: "scene_generator_variant" }),
          });
          const applied = applyAssetCreateResult(v, res, assetProvider);
          if (applied.accepted) {
            variations[i] = applied.target;
          }
        } catch { /* skip */ }
      }
      updated.variations = variations;

      await saveSceneAsset(updated, {
        rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
      });
      await fetchScenes();
      const meta = getEffectiveAssetMeta(updated, assetProvider);
      toast.success(`${assetLibraryLabel(meta.provider)}入库已提交，等待处理`);
      pollAssetStatus(updated);
    } catch (e) { toast.error("入库失败: " + e.message); }
    finally {
      setUploadingAssetIds(prev => { const next = new Set(prev); next.delete(sceneId); return next; });
    }
  };

  const pollAssetStatus = (entity) => {
    if (!assetIngestSupported) return;
    const entityId = entity.id;
    if (pollingIdsRef.current.has(entityId)) return;
    const token = createLocalToken();
    pollingIdsRef.current.set(entityId, token);

    const ids = [];
    const mainMeta = getEffectiveAssetMeta(entity, assetProvider);
    const mainAssetId = mainMeta.assetId;
    const mainStatus = mainMeta.status;
    if (mainAssetId && mainStatus === "Processing") {
      ids.push({ assetId: mainAssetId, provider: mainMeta.provider, target: "main", url: entity.imageUrl });
    }
    (entity.variations || []).forEach((v, i) => {
      const meta = getEffectiveAssetMeta(v, assetProvider);
      const assetId = meta.assetId;
      const status = meta.status;
      if (assetId && status === "Processing") {
        ids.push({ assetId, provider: meta.provider, target: "variant", index: i, url: v.imageUrl });
      }
    });
    if (ids.length === 0) { stopAssetPolling(entityId); return; }

    const poll = async () => {
      if (pollingIdsRef.current.get(entityId) !== token) return;
      let allDone = true;
      const updated = { ...entity };
      const variations = [...(updated.variations || [])];
      for (const item of ids) {
        if (pollingIdsRef.current.get(entityId) !== token) return;
        try {
          const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/status`, {
            method: "POST",
            body: JSON.stringify({
              projectId: projectId ? Number(projectId) : undefined,
              url: item.url,
              assetId: item.assetId,
            }),
          });
          if (pollingIdsRef.current.get(entityId) !== token) return;
          const currentTarget = item.target === "main" ? updated : variations[item.index];
          const applied = applyAssetStatusResult(currentTarget, res, assetProvider);
          if (!applied.accepted) {
            allDone = false;
            continue;
          }
          const status = applied.status;
          if (item.target === "main") {
            Object.assign(updated, applied.target);
          } else {
            variations[item.index] = applied.target;
          }
          if (status === "Processing") allDone = false;
        } catch { allDone = false; }
      }
      updated.variations = variations;
      try {
        if (pollingIdsRef.current.get(entityId) !== token) return;
        await saveSceneAsset(updated, {
          shouldApply: () => pollingIdsRef.current.get(entityId) === token,
          rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
        });
        if (pollingIdsRef.current.get(entityId) !== token) return;
      } catch { /* ignore */ }
      if (!allDone) {
        scheduleAssetPoll(
          entityId,
          token,
          poll,
          3000,
        );
      } else {
        if (pollingIdsRef.current.get(entityId) === token) {
          stopAssetPolling(entityId);
        }
        fetchScenes();
      }
    };
    scheduleAssetPoll(
      entityId,
      token,
      poll,
      3000,
    );
  };

  // 保存别名
  const handleSaveAlias = async () => {
    if (!selectedScene) return;
    try {
      const aliasArray = editAliasText.split(/[,，]/).map(s => s.trim()).filter(Boolean);
      await saveSceneAsset({ ...selectedScene, aliases: JSON.stringify(aliasArray) });
      await fetchScenes();
      setEditingAlias(false);
      toast.success("别名已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 保存描述
  const handleSaveDescription = async () => {
    if (!selectedScene) return;
    try {
      await saveSceneAsset({ ...selectedScene, description: editDescriptionText });
      await fetchScenes();
      setEditingDescription(false);
      toast.success("描述已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 变体微调（基于主体图 + 变体图 + 修改描述）
  const handleRefineVariant = async (idx) => {
    const variant = sceneVariations[idx];
    if (!selectedScene || !variant?.imageUrl) return;
    if (!variantRefinePrompt.trim()) return toast.warning("请填写修改描述");
    const sceneId = selectedScene.id;
    const variantKey = `${sceneId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    try {
      const newImageUrl = await runWorkshopImageTaskToUrl("GENERATE_SCENE_VARIANT", {
        originalPrompt: selectedScene.description || "",
        baseImageUrl: variant.imageUrl,
        variantPrompt: variantRefinePrompt,
        name: variant.title || selectedScene.name,
        projectId,
        imageQualityTier,
      }, { styleAnchor: currentStyle, projectId, source: "scene_variant" });
      const updatedVariations = sceneVariations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(sceneId);
      await saveSceneAsset({ ...selectedScene, variations: updatedVariations });
      await fetchScenes();
      setShowVariantRefinePanel(false);
      setVariantRefinePrompt("");
      toast.success("变体微调完成！");
    } catch (e) { toast.error("微调失败: " + e.message); }
    finally {
      setRegeneratingVariantKeys(prev => { const next = new Set(prev); next.delete(variantKey); return next; });
    }
  };

  // 直接上传新变体，不触发图片生成或素材入库。
  const handleUploadNewVariant = async ({ assetId, file, title, description }) => {
    const uploadProjectId = projectId;
    const saved = await uploadWorkshopVariant({
      assetId,
      projectId: uploadProjectId,
      file,
      title,
      description,
      uploadFile: async (uploadFile) => {
        const formData = new FormData();
        formData.append("file", uploadFile);
        return authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      },
      getAsset: (id) => isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId) ? getSceneById(id) : null,
      saveAsset: (asset, rebase) => saveSceneAsset(asset, {
        rebase,
        shouldApply: () => isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId),
      }),
      onBeforeSave: () => stopAssetPolling(assetId),
    });
    if (isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId)) await fetchScenes();
    return saved;
  };

  // 变体替换图片
  const handleReplaceVariantImage = async (e, idx) => {
    const input = e.target;
    const file = input.files[0];
    const originalVariant = sceneVariations[idx];
    if (!file || !selectedScene || !originalVariant) return;
    const sceneId = selectedScene.id;
    const uploadProjectId = projectId;
    const originalImageUrl = originalVariant.imageUrl;
    const originalTitle = originalVariant.title;
    const originalDescription = originalVariant.description;
    const variantKey = `${sceneId}-${idx}`;
    setRegeneratingVariantKeys(prev => new Set(prev).add(variantKey));
    const formData = new FormData();
    formData.append("file", file);
    try {
      validateVariantImageFile(file);
      const newImageUrl = await authenticatedFetch(`${API_BASE}/api/upload/asset`, { method: "POST", body: formData });
      const latestScene = getSceneById(sceneId);
      if (!isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId)
        || !latestScene || (latestScene.projectId != null && !isSameWorkshopAssetId(latestScene.projectId, uploadProjectId))) {
        throw new Error("场景已删除或项目已切换，请在原项目重新上传");
      }
      const latestVariant = latestScene.variations?.[idx];
      if (!latestVariant || latestVariant.imageUrl !== originalImageUrl
        || latestVariant.title !== originalTitle || latestVariant.description !== originalDescription) {
        throw new Error("该变体已修改或删除，请重新选择要上传的变体");
      }
      const updatedVariations = latestScene.variations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v);
      stopAssetPolling(sceneId);
      await saveSceneAsset({ ...latestScene, variations: updatedVariations }, {
        shouldApply: () => isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId),
        rebase: currentAsset => {
          const currentVariant = currentAsset.variations?.[idx];
          if (!currentVariant || currentVariant.imageUrl !== originalImageUrl
            || currentVariant.title !== originalTitle || currentVariant.description !== originalDescription) {
            throw new Error("该变体已修改或删除，请重新选择要上传的变体");
          }
          return {
            ...currentAsset,
            variations: currentAsset.variations.map((v, i) => i === idx ? { ...clearVideoAssetBinding(v), imageUrl: newImageUrl } : v),
          };
        },
      });
      if (isSameWorkshopAssetId(currentProjectIdRef.current, uploadProjectId)) await fetchScenes();
      toast.success(originalImageUrl ? "变体图片已替换" : "变体图片已上传");
    } catch (err) { toast.error("上传失败: " + err.message); }
    finally {
      setRegeneratingVariantKeys(prev => { const next = new Set(prev); next.delete(variantKey); return next; });
      if (input) input.value = "";
    }
  };

  // 变体单独入库
  const handleUploadVariantToAssets = async (idx) => {
    if (!assetIngestSupported) return toast.info("当前项目视频服务不支持素材入库");
    const variant = sceneVariations[idx];
    if (!selectedScene || !variant?.imageUrl) return toast.warning("没有变体图，无法入库");
    const sceneId = selectedScene.id;
    setUploadingAssetIds(prev => new Set(prev).add(`${sceneId}-v${idx}`));
    try {
      const res = await authenticatedFetch(`${API_BASE}${assetApiBase}/create`, {
        method: "POST",
        body: JSON.stringify({ projectId, url: variant.imageUrl, name: `${selectedScene.name || "场景"}-${variant.title || `变体${idx + 1}`}`, assetType: "Image", typeCode: "scene_image", source: "asset", sourceAssetId: sceneId, sourceDetail: "scene_generator_variant" }),
      });
      const applied = applyAssetCreateResult(variant, res, assetProvider);
      if (applied.accepted) {
        const updatedVariations = sceneVariations.map((v, i) => i === idx ? applied.target : v);
        const updated = { ...selectedScene, variations: updatedVariations };
        await saveSceneAsset(updated, {
          rebase: latest => mergeWorkshopAssetRegistration(latest, updated, getVideoAssetFieldNames(assetProvider)),
        });
        await fetchScenes();
        pollAssetStatus(updated);
        toast.success(`${assetLibraryLabel(applied.provider)}变体入库已提交`);
      }
    } catch (e) { toast.error("入库失败: " + e.message); }
    finally {
      setUploadingAssetIds(prev => { const next = new Set(prev); next.delete(`${sceneId}-v${idx}`); return next; });
    }
  };

  // 保存变体描述
  const handleSaveVariantDescription = async (idx) => {
    if (!selectedScene) return;
    try {
      const updatedVariations = sceneVariations.map((v, i) => i === idx ? { ...v, description: editVariantDescText } : v);
      await saveSceneAsset({ ...selectedScene, variations: updatedVariations });
      await fetchScenes();
      setEditingVariantDesc(false);
      toast.success("变体描述已更新");
    } catch (e) { toast.error("更新失败: " + e.message); }
  };

  // 删除场景
  const handleDelete = async () => {
    if (!deleteTargetId) return;
    try {
      await authenticatedFetch(`${API_BASE}/api/scenes/${deleteTargetId}`, { method: "DELETE" });
      setScenes(prev => prev.filter(s => s.id !== deleteTargetId));
      if (selectedSceneId === deleteTargetId) setSelectedSceneId(null);
      toast.success("已移入回收站，可联系管理员恢复");
    } catch (e) { toast.error("删除失败: " + e.message); } finally { setDeleteTargetId(null); }
  };

  const handleRename = async (name) => {
    if (!renameTarget?.id) return;
    const id = renameTarget.id;
    setRenamingId(id);
    try {
      const updated = await authenticatedFetch(`${API_BASE}/api/scenes/${id}/name`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      });
      setScenes(prev => prev.map(s => s.id === id ? updated : s));
      toast.success("场景已更名");
      setRenameTarget(null);
    } catch (e) {
      toast.error("更名失败: " + e.message);
    } finally {
      setRenamingId(null);
    }
  };

  return (
    <div className="workshop-shell w-full h-full flex flex-col font-sans" style={{ color: 'var(--color-text-primary)', backgroundColor: 'var(--color-bg-primary)' }}>

      {/* 大图预览 */}
      {previewImage && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-8 animate-in fade-in" onClick={() => setPreviewImage(null)} style={{ backgroundColor: 'var(--overlay-heavy)', backdropFilter: 'blur(12px)' }}>
          <button className="absolute top-6 right-6 p-2 rounded-full" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-primary)' }}><X size={24} /></button>
          <img src={previewImage} className="max-w-full max-h-full object-contain rounded-xl animate-in fade-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()} />
        </div>
      )}

      {/* 顶部栏 */}
      <div className="workshop-header h-14 border-b flex items-center justify-between px-6 shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
        <h2 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
          <span className="workshop-title-icon p-1.5 rounded-lg"><WorkshopArtwork kind="scene" size={16} /></span>
          场景工坊
        </h2>
        <div className="workshop-toolbar flex items-center gap-2">
          <ModelArkLibraryToolbar registry={modelArkRegistry} />
          <AppSelect
            value={imageQualityTier}
            onChange={(e) => handleImageQualityTierChange(e.target.value)}
            className="px-2.5 py-1.5 rounded-lg text-xs font-bold outline-none"
            title="图片清晰度档位"
            style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border-primary)' }}
          >
            {availableImageQualityTierOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
          </AppSelect>
          {scenes.filter(s => s.description && s.description !== "场景图" && !s.imageUrl).length > 0 && (
            <button
              onClick={handleBatchGenerate}
              disabled={batchGenerating}
              className="workshop-primary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs"
              style={{ background: batchGenerating ? 'var(--color-bg-tertiary)' : 'var(--color-accent-primary)', color: 'white', opacity: batchGenerating ? 0.7 : 1 }}
            >
              {batchGenerating ? <><Loader2 size={13} className="animate-spin" /> {batchProgress.done}/{batchProgress.total}</> : <><Wand2 size={13} /> 一键生成</>}
            </button>
          )}
          <button
            onClick={() => { setIsCreating(true); setIsGenerating(false); setIsUploading(false); setNewSceneName(''); setNewScenePrompt(''); setNewSceneAliases(''); setNewSceneMode('single'); setReferenceImages([null, null, null, null, null]); }}
            className="workshop-primary-action flex items-center gap-1.5 px-4 py-1.5 rounded-lg font-bold text-xs"
            style={{ background: 'var(--color-accent-primary)', color: 'white' }}
          >
            <Plus size={13} /> 新建场景
          </button>
        </div>
      </div>

      {/* 主体区域：左右分栏 */}
      <div className="workshop-body flex-1 flex min-h-0">

        {/* 左侧：场景列表 */}
        <div className="workshop-sidebar w-56 border-r overflow-y-auto custom-scrollbar p-3 space-y-2" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-primary)' }}>
          {scenes.map(scene => (
            <div
              key={scene.id}
              onClick={() => setSelectedSceneId(scene.id)}
              className="workshop-asset-card group relative rounded-lg overflow-hidden cursor-pointer"
              data-selected={selectedSceneId === scene.id}
            >
              <div className={'aspect-[21/9]'} style={{ overflow: 'hidden', backgroundColor: 'var(--color-bg-overlay)' }}>
                <img src={scene.imageUrl} className="w-full h-full object-contain" alt={scene.name} />
              </div>
              <div className="workshop-asset-caption p-2">
                <div className="flex items-center gap-1">
                  <h3 className="text-xs font-bold truncate" style={{ color: 'var(--color-text-primary)' }}>{scene.name}</h3>
                  {scene.sceneMode === 'multiAngle' && <LayoutGrid size={10} style={{ color: theme.colors.accentPrimary, flexShrink: 0 }} />}
                </div>
                {scene.variations && scene.variations.length > 0 && (
                  <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{scene.variations.length} 个变体</p>
                )}
              </div>
              <button
                onClick={(e) => { e.stopPropagation(); setRenameTarget(scene); }}
                className="workshop-card-action absolute top-1.5 left-1.5 p-1 rounded-md"
                style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                title="更名"
                aria-label={`更名 ${scene.name || "场景"}`}
              >
                {renamingId === scene.id ? <Loader2 size={11} className="animate-spin" /> : <Edit2 size={11} />}
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setDeleteTargetId(scene.id); }}
                className="workshop-card-action absolute top-1.5 right-1.5 p-1 rounded-md"
                style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                aria-label={`删除场景 ${scene.name || '未命名场景'}`}
              >
                <Trash2 size={11} />
              </button>
            </div>
          ))}
          {scenes.length === 0 && (
            <div className="workshop-list-empty flex flex-col items-center justify-center py-16">
              <WorkshopArtwork kind="scene" size={28} className="mb-2" />
              <p className="text-2xs text-center" style={{ color: 'var(--color-text-muted)' }}>暂无场景<br/>点击右上角新建</p>
            </div>
          )}
        </div>

        {/* 右侧：详情面板 */}
        <div className="workshop-detail flex-1 overflow-y-auto custom-scrollbar">
          {selectedScene ? (
            <div className="workshop-detail-content p-6 max-w-6xl mx-auto space-y-5">

              {/* 主体场景图区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-4">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>主体场景图</h3>
                  <div className="workshop-section-actions flex items-center gap-2">
                    <input type="file" ref={replaceMainInputRef} className="hidden" accept="image/*" onChange={handleReplaceMainImage} />
                    <button
                      onClick={() => replaceMainInputRef.current?.click()}
                      disabled={isReplacingMain}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                    >
                      {isReplacingMain ? <Loader2 size={10} className="animate-spin" /> : <Upload size={10} />}
                      {isReplacingMain ? '上传中...' : '替换'}
                    </button>
                    <button
                      onClick={handleRegenerateMain}
                      disabled={isRegeneratingMain}
                      className="workshop-primary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium disabled:opacity-50"
                      style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                    >
                      {isRegeneratingMain ? <Loader2 size={10} className="animate-spin" /> : <Wand2 size={10} />}
                      重新生成
                    </button>
                    {selectedScene.imageUrl && (
                      <button
                        onClick={() => { setShowRefinePanel(v => !v); setRefinePrompt(""); }}
                        disabled={isRefiningMain}
                        className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                        data-active={showRefinePanel} style={{
                          backgroundColor: showRefinePanel ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}20`,
                          color: showRefinePanel ? 'white' : theme.colors.accentPrimary,
                          border: `1px solid ${theme.colors.accentPrimary}`
                        }}
                      >
                        {isRefiningMain ? <Loader2 size={10} className="animate-spin" /> : <Edit2 size={10} />}
                        微调
                      </button>
                    )}
                    {selectedScene?.sceneMode === 'multiAngle' && (
                      <button
                        onClick={handleSplitToVariants}
                        disabled={isSplittingVariants || !selectedScene?.imageUrl}
                        className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                        style={{ background: 'var(--color-accent-primary)', color: 'white' }}
                      >
                        {isSplittingVariants ? <Loader2 size={10} className="animate-spin" /> : <LayoutGrid size={10} />}
                        {isSplittingVariants ? '裁切中...' : '裁切16变体'}
                      </button>
                    )}
                    <ModelArkImageLibraryStatus registry={modelArkRegistry} url={selectedScene.imageUrl} />
                    {assetIngestSupported && selectedScene.imageUrl && (() => {
                      const meta = getEffectiveAssetMeta(selectedScene, assetProvider);
                      const status = meta.status;
                      const isUploading = uploadingAssetIds.has(selectedScene.id);
                      const isProcessing = status === "Processing";
                      const isReady = isAssetReadyStatus(status);
                      const isFailed = status === "Failed";
                      const disabled = isUploading || isProcessing || isReady;
                      return (
                        <button
                          onClick={handleUploadToAssets}
                          disabled={disabled}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-60"
                          style={{
                            backgroundColor: isReady ? `${theme.colors.success}20` : isFailed ? `${theme.colors.error}20` : `${theme.colors.accentPrimary}20`,
                            color: isReady ? theme.colors.success : isFailed ? theme.colors.error : theme.colors.accentPrimary,
                            border: `1px solid ${isReady ? theme.colors.success : isFailed ? theme.colors.error : theme.colors.accentPrimary}`,
                          }}
                          title={meta.assetId ? `${assetLibraryLabel(meta.provider)}：${meta.assetId}` : `提交到${assetLibraryLabel(meta.provider)}`}
                        >
                          {isUploading || isProcessing ? <Loader2 size={10} className="animate-spin" /> : isReady ? <Check size={10} /> : <PackagePlus size={10} />}
                          {isUploading ? '入库中...' : assetStatusLabel(status, isFailed ? '入库失败' : '素材入库')}
                        </button>
                      );
                    })()}
                  </div>
                </div>

                {/* 微调面板 */}
                {showRefinePanel && selectedScene.imageUrl && (
                  <div className="mb-4 p-4 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}` }}>
                    <label className="text-2xs font-medium block" style={{ color: 'var(--color-text-muted)' }}>修改描述（基于当前图微调）</label>
                    <textarea
                      ref={refinePromptTextareaRef}
                      className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                      style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                      rows={2}
                      value={refinePrompt}
                      onChange={e => {
                        setRefinePrompt(e.target.value);
                        const textarea = e.target;
                        textarea.style.height = 'auto';
                        textarea.style.height = textarea.scrollHeight + 'px';
                      }}
                      onFocus={e => {
                        const textarea = e.target;
                        textarea.style.height = 'auto';
                        textarea.style.height = textarea.scrollHeight + 'px';
                      }}
                      onBlur={e => {
                        const textarea = e.target;
                        textarea.style.height = 'auto';
                        textarea.style.height = textarea.scrollHeight + 'px';
                      }}
                      placeholder="例如：改成夜晚、加一些雾气、换成下雨天..."
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => { setShowRefinePanel(false); setRefinePrompt(""); }}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >取消</button>
                      <button
                        onClick={handleRefineMain}
                        disabled={isRefiningMain || !refinePrompt.trim()}
                        className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        {isRefiningMain ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                        {isRefiningMain ? "微调中..." : "开始微调"}
                      </button>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {selectedScene.imageUrl ? (
                    <div className="workshop-preview relative group aspect-[21/9] rounded-lg overflow-hidden cursor-zoom-in" aria-busy={isRegeneratingMain || isRefiningMain} style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(selectedScene.imageUrl)}>
                      <img src={selectedScene.imageUrl} className="w-full h-full object-contain" alt={selectedScene.name} />
                      {(isRegeneratingMain || isRefiningMain) && (
                        <div className="workshop-preview-status" role="status">
                          <span className="workshop-preview-status-dot" aria-hidden="true" />
                          <span>{isRefiningMain ? '微调中' : '重新生成中'} · 显示上一版</span>
                        </div>
                      )}
                        <>
                          {/* 下载按钮 */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const link = document.createElement('a');
                              link.href = selectedScene.imageUrl;
                              link.download = `${selectedScene.name || '场景'}.png`;
                              document.body.appendChild(link);
                              link.click();
                              document.body.removeChild(link);
                            }}
                            className="workshop-preview-action absolute top-2 right-2 z-10 w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: 'var(--overlay-medium)' }}
                            title="下载图片"
                          >
                            <Download size={16} className="text-white" />
                          </button>
                        </>
                    </div>
                  ) : (
                    <div className="aspect-[21/9] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-3" style={{ borderColor: 'var(--color-border-primary)', backgroundColor: 'var(--color-bg-overlay)' }}>
                      <Sparkles size={32} style={{ color: 'var(--color-text-muted)', opacity: 0.4 }} />
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>尚未生成场景图</p>
                      <div className="flex gap-2">
                        <button
                          onClick={handleRegenerateMain}
                          disabled={isRegeneratingMain || !selectedScene.description}
                          className="workshop-primary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold disabled:opacity-50"
                          style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                        >
                          {isRegeneratingMain ? <Loader2 size={12} className="animate-spin" /> : <Wand2 size={12} />}
                          AI 生成
                        </button>
                        <button
                          onClick={() => replaceMainInputRef.current?.click()}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}
                        >
                          <Upload size={12} /> 上传图片
                        </button>
                      </div>
                    </div>
                  )}

                  {/* 基本信息 */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>场景名称</label>
                      <div className="text-base font-bold" style={{ color: 'var(--color-text-primary)' }}>{selectedScene.name}</div>
                    </div>
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>别名（智能匹配）</label>
                        {!editingAlias && (
                          <button onClick={() => { setEditingAlias(true); setEditAliasText(sceneAliases.join(', ')); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                            <Edit2 size={9} /> 编辑
                          </button>
                        )}
                      </div>
                      {editingAlias ? (
                        <div className="flex gap-1.5">
                          <input
                            className="flex-1 rounded-md px-2 py-1.5 text-xs outline-none"
                            style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)' }}
                            value={editAliasText}
                            onChange={e => setEditAliasText(e.target.value)}
                            placeholder="教室, 班级, 课室..."
                          />
                          <button onClick={handleSaveAlias} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                          <button onClick={() => setEditingAlias(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {sceneAliases.length > 0
                            ? sceneAliases.map((a, i) => <span key={i} className="text-2xs px-2 py-0.5 rounded" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>{a}</span>)
                            : <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>暂无别名</span>
                          }
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>原始描述（AI 生成提示词）</label>
                      {!editingDescription && (
                        <button onClick={() => { setEditingDescription(true); setEditDescriptionText(selectedScene.description || ""); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                          <Edit2 size={9} /> 编辑
                        </button>
                      )}
                    </div>
                    {editingDescription ? (
                      <div className="space-y-2">
                        <textarea
                          ref={editDescriptionTextareaRef}
                          rows={3}
                          className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                          style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                          value={editDescriptionText}
                          onChange={e => {
                            setEditDescriptionText(e.target.value);
                            const textarea = e.target;
                            textarea.style.height = 'auto';
                            textarea.style.height = textarea.scrollHeight + 'px';
                          }}
                          onFocus={e => {
                            const textarea = e.target;
                            textarea.style.height = 'auto';
                            textarea.style.height = textarea.scrollHeight + 'px';
                          }}
                          onBlur={e => {
                            const textarea = e.target;
                            textarea.style.height = 'auto';
                            textarea.style.height = textarea.scrollHeight + 'px';
                          }}
                          placeholder="描述场景环境、光线、氛围..."
                        />
                        <div className="flex justify-end gap-1.5">
                          <button onClick={handleSaveDescription} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                          <button onClick={() => setEditingDescription(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs p-3 rounded-lg leading-relaxed" style={{ backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-secondary)' }}>
                        {selectedScene.description || "无描述"}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* 参考图展示区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-3">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>参考图</h3>
                  {!editingDetailRefs && (
                    <button
                      onClick={handleStartEditDetailRefs}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                    >
                      <Edit2 size={10} /> 编辑
                    </button>
                  )}
                </div>

                {editingDetailRefs ? (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                      {detailRefImages.filter(Boolean).map((img, idx) => {
                        const actualIndex = detailRefImages.indexOf(img);
                        return (
                          <div
                            key={actualIndex}
                            className="relative w-24 h-24 rounded-lg overflow-hidden cursor-zoom-in group"
                            style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)' }}
                            onClick={() => setPreviewImage(img)}
                          >
                            <img src={img} className="w-full h-full object-cover" alt={`参考图${idx + 1}`} />
                            <button
                              onClick={(e) => { e.stopPropagation(); handleRemoveDetailRefImage(actualIndex); }}
                              className="absolute top-1 right-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                              style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                            >
                              <X size={10} />
                            </button>
                            <div className="absolute bottom-0 left-0 right-0 text-center text-2xs py-0.5 font-bold" style={{ background: 'var(--overlay-medium)', color: 'white' }}>
                              图{idx + 1}
                            </div>
                          </div>
                        );
                      })}
                      {detailRefImages.filter(Boolean).length < maxReferenceImages && (
                        <>
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*"
                            ref={el => detailRefInputRefs.current[0] = el}
                            onChange={(e) => {
                              const firstEmptyIndex = detailRefImages.findIndex(img => !img);
                              if (firstEmptyIndex !== -1) handleUploadDetailRefImage(e, firstEmptyIndex);
                            }}
                          />
                          <button
                            onClick={() => detailRefInputRefs.current[0]?.click()}
                            disabled={uploadingDetailRefIndex !== null}
                            className="w-24 h-24 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 workshop-control-feedback hover:border-solid disabled:opacity-50"
                            style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
                          >
                            {uploadingDetailRefIndex !== null ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <>
                                <Plus size={20} />
                                <span className="text-2xs">添加</span>
                              </>
                            )}
                          </button>
                        </>
                      )}
                    </div>
                    <p className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                      最多{maxReferenceImages}张，在描述中写"参考图1的建筑风格"、"参考图2的光照"等来引用
                    </p>
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => setEditingDetailRefs(false)}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handleSaveDetailRefs}
                        className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        <Check size={11} /> 保存
                      </button>
                    </div>
                  </div>
                ) : (
                  (() => {
                    const savedRefs = (() => { try { return JSON.parse(selectedScene.referenceImages || '[]'); } catch { return []; } })();
                    if (savedRefs.length === 0) {
                      return (
                        <div className="flex flex-col items-center justify-center py-8">
                          <ImagePlus size={24} className="mb-2 opacity-20" style={{ color: 'var(--color-text-muted)' }} />
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无参考图，点击"编辑"添加</p>
                        </div>
                      );
                    }
                    return (
                      <div className="flex flex-wrap gap-2">
                        {savedRefs.map((url, idx) => (
                          <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden cursor-zoom-in" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)' }} onClick={() => setPreviewImage(url)}>
                            <img src={url} className="w-full h-full object-cover" alt={`参考图${idx + 1}`} />
                            <div className="absolute bottom-0 left-0 right-0 text-center text-2xs py-0.5 font-bold" style={{ background: 'var(--overlay-medium)', color: 'white' }}>
                              图{idx + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>

              {/* 场景变体区 */}
              <div className="workshop-panel rounded-xl p-5" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
                <div className="workshop-section-heading flex items-center justify-between mb-4">
                  <h3 className="workshop-section-title text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>场景变体</h3>
                  <div className="workshop-section-actions flex flex-wrap items-center gap-2">
                    <button
                      onClick={() => { setShowCameraPanel(!showCameraPanel); if (!showCameraPanel) setShowVariantPanel(false); }}
                      className="workshop-secondary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs workshop-control-feedback"
                      data-active={showCameraPanel} style={{ backgroundColor: showCameraPanel ? 'var(--color-bg-tertiary)' : theme.colors.accentPrimary, color: showCameraPanel ? 'var(--color-text-muted)' : 'white' }}
                    >
                      {showCameraPanel ? <X size={11} /> : <Camera size={11} />}
                      {showCameraPanel ? '取消' : '摄像机'}
                    </button>
                    <button
                      onClick={() => { setShowVariantPanel(!showVariantPanel); if (!showVariantPanel) setShowCameraPanel(false); }}
                      className="workshop-secondary-action flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-xs workshop-control-feedback"
                      data-active={showVariantPanel} style={{ backgroundColor: showVariantPanel ? 'var(--color-bg-tertiary)' : theme.colors.accentPrimary, color: showVariantPanel ? 'var(--color-text-muted)' : 'white' }}
                    >
                      {showVariantPanel ? <X size={11} /> : <Plus size={11} />}
                      {showVariantPanel ? '取消' : '生成变体'}
                    </button>
                    <WorkshopVariantUpload
                      key={`${projectId}-${selectedScene.id}`}
                      assetId={selectedScene.id}
                      assetName={selectedScene.name}
                      assetLabel="场景"
                      disabled={isGeneratingVariant}
                      onUpload={handleUploadNewVariant}
                    />
                  </div>
                </div>

                {/* 摄像机控制面板 */}
                {showCameraPanel && (
                  <div className="mb-4 p-4 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}` }}>
                    <CameraController
                      azimuth={cameraAzimuth}
                      elevation={cameraElevation}
                      distance={cameraDistance}
                      imageUrl={selectedScene?.imageUrl}
                      onChange={({ azimuth, elevation, distance }) => {
                        setCameraAzimuth(azimuth);
                        setCameraElevation(elevation);
                        setCameraDistance(distance);
                      }}
                    />
                    <div className="flex justify-end gap-2 pt-1">
                      <button
                        onClick={() => setShowCameraPanel(false)}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handleGenerateCameraVariant}
                        disabled={isGeneratingCameraVariant}
                        className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        {isGeneratingCameraVariant ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                        {isGeneratingCameraVariant ? "生成中..." : "生成此角度"}
                      </button>
                    </div>
                  </div>
                )}

                {/* 变体生成面板 */}
                {showVariantPanel && (
                  <div className="mb-4 p-4 rounded-lg space-y-3 animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-bg-primary)', border: `1px solid ${theme.colors.accentPrimary}` }}>
                    <div>
                      <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>变体名称</label>
                      <input
                        className="w-full rounded-md px-3 py-2 text-xs outline-none"
                        style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                        value={variantName}
                        onChange={e => setVariantName(e.target.value)}
                        placeholder="例如：夜晚、冬天、雨天"
                      />
                    </div>
                    <div>
                      <label className="text-2xs font-medium mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>变体描述（在原场景基础上的变化）</label>
                      <textarea
                        ref={variantPromptTextareaRef}
                        rows={2}
                        className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                        style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                        value={variantPrompt}
                        onChange={e => {
                          setVariantPrompt(e.target.value);
                          const textarea = e.target;
                          textarea.style.height = 'auto';
                          textarea.style.height = textarea.scrollHeight + 'px';
                        }}
                        onFocus={e => {
                          const textarea = e.target;
                          textarea.style.height = 'auto';
                          textarea.style.height = textarea.scrollHeight + 'px';
                        }}
                        onBlur={e => {
                          const textarea = e.target;
                          textarea.style.height = 'auto';
                          textarea.style.height = textarea.scrollHeight + 'px';
                        }}
                        placeholder="例如：夜晚，灯光昏暗，月光照射；或：冬天，白雪覆盖，寒冷氛围"
                      />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <button
                        onClick={() => { setShowVariantPanel(false); setVariantName(""); setVariantPrompt(""); }}
                        className="px-3 py-1.5 rounded-md text-xs"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}
                      >
                        取消
                      </button>
                      <button
                        onClick={handleGenerateVariant}
                        disabled={isGeneratingVariant || !variantName.trim() || !variantPrompt.trim()}
                        className="workshop-primary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                        style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                      >
                        {isGeneratingVariant ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                        {isGeneratingVariant ? "生成中..." : "生成变体"}
                      </button>
                    </div>
                  </div>
                )}

                {/* 变体列表（手风琴） */}
                <div className="space-y-2">
                  {sceneVariations.map((v, vi) => {
                    const isExpanded = expandedVariantIndex === vi;
                    const isRegenerating = getRegeneratingVariantIndex(vi);
                    const vMeta = getEffectiveAssetMeta(v, assetProvider);
                    const vStatus = vMeta.status;
                    const isVUploading = uploadingAssetIds.has(`${selectedScene.id}-v${vi}`);
                    const isVProcessing = vStatus === "Processing";
	                    const isVReady = isAssetReadyStatus(vStatus);
	                    const isVFailed = vStatus === "Failed";

                    return (
                      <div key={vi} data-expanded={isExpanded} className="workshop-variant-card rounded-lg overflow-hidden" style={{ border: `1px solid ${isExpanded ? theme.colors.accentPrimary : 'var(--color-border-primary)'}`, backgroundColor: 'var(--color-bg-primary)' }}>
                        <div
                          className="workshop-variant-heading flex items-center gap-3 px-3 py-2 cursor-pointer workshop-control-feedback"
                          style={{ backgroundColor: isExpanded ? `${theme.colors.accentPrimary}10` : 'transparent' }}
                          onClick={() => {
                            setExpandedVariantIndex(isExpanded ? null : vi);
                            setShowVariantRefinePanel(false);
                            setVariantRefinePrompt("");
                            setEditingVariantDesc(false);
                          }}
                        >
                          <ChevronRight
                            size={14}
                            className="ui-accordion-chevron"
                            data-open={isExpanded}
                            style={{ color: isExpanded ? theme.colors.accentPrimary : 'var(--color-text-muted)' }}
                          />
                          <div className="w-16 h-7 rounded overflow-hidden shrink-0" style={{ backgroundColor: 'var(--color-bg-overlay)' }}>
                            {v.imageUrl && <img src={v.imageUrl} className="w-full h-full object-cover" alt={v.title} />}
                          </div>
                          <span className="text-xs font-bold flex-1 truncate" style={{ color: 'var(--color-text-primary)' }}>{v.title}</span>
                          {isRegenerating && <Loader2 size={12} className="animate-spin" style={{ color: theme.colors.bluePrimary }} />}
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteVariantIndex(vi); }}
                            className="workshop-variant-delete p-1 rounded-md workshop-control-feedback"
                            style={{ color: 'var(--color-text-muted)' }}
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>

                        <div className="ui-accordion-grid" data-open={isExpanded}>
                          <div className="ui-accordion-content" inert={!isExpanded} aria-hidden={!isExpanded}>
                            <div className="px-4 pb-4 space-y-3" style={{ borderTop: '1px solid var(--color-border-primary)' }}>
                            <div className="flex flex-wrap items-center gap-2 pt-3">
                              <input
                                type="file"
                                ref={(node) => {
                                  if (node) variantReplaceInputRefs.current.set(vi, node);
                                  else variantReplaceInputRefs.current.delete(vi);
                                }}
                                className="hidden"
                                accept="image/*"
                                onChange={(e) => handleReplaceVariantImage(e, vi)}
                              />
                              <button
                                onClick={() => variantReplaceInputRefs.current.get(vi)?.click()}
                                disabled={isRegenerating}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border-primary)' }}
                              >
                                <Upload size={10} /> {v.imageUrl ? '替换图片' : '上传图片'}
                              </button>
                              <button
                                onClick={() => handleRegenerateVariant(vi)}
                                disabled={isRegenerating}
                                className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                style={{ backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `1px solid ${theme.colors.accentPrimary}` }}
                              >
                                {isRegenerating ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
                                重新生成
                              </button>
                              {v.imageUrl && (
                                <button
                                  onClick={() => { setShowVariantRefinePanel(p => !p); setVariantRefinePrompt(""); }}
                                  disabled={isRegenerating}
                                  className="workshop-secondary-action flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-50"
                                  data-active={showVariantRefinePanel} style={{
                                    backgroundColor: showVariantRefinePanel ? theme.colors.accentPrimary : `${theme.colors.accentPrimary}20`,
                                    color: showVariantRefinePanel ? 'white' : theme.colors.accentPrimary,
                                    border: `1px solid ${theme.colors.accentPrimary}`
                                  }}
                                >
                                  <Edit2 size={10} /> 微调
                                </button>
                              )}
                              <ModelArkImageLibraryStatus registry={modelArkRegistry} url={v.imageUrl} />
                              {assetIngestSupported && v.imageUrl && (() => {
                                const disabled = isVUploading || isVProcessing || isVReady;
                                return (
                                  <button
                                    onClick={() => handleUploadVariantToAssets(vi)}
                                    disabled={disabled}
                                    className="flex items-center gap-1 px-2.5 py-1 rounded-md text-2xs font-medium workshop-control-feedback disabled:opacity-60"
                                    style={{
                                      backgroundColor: isVReady ? `${theme.colors.success}20` : isVFailed ? `${theme.colors.error}20` : `${theme.colors.accentPrimary}20`,
                                      color: isVReady ? theme.colors.success : isVFailed ? theme.colors.error : theme.colors.accentPrimary,
                                      border: `1px solid ${isVReady ? theme.colors.success : isVFailed ? theme.colors.error : theme.colors.accentPrimary}`,
                                    }}
                                  >
                                    {isVUploading || isVProcessing ? <Loader2 size={10} className="animate-spin" /> : isVReady ? <Check size={10} /> : <PackagePlus size={10} />}
                                    {isVUploading ? '入库中...' : assetStatusLabel(vStatus, isVFailed ? '入库失败' : '素材入库')}
                                  </button>
                                );
                              })()}
                            </div>

                            {isExpanded && showVariantRefinePanel && v.imageUrl && (
                              <div className="p-3 rounded-lg space-y-2 animate-in fade-in slide-in-from-top-2 duration-200" style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${theme.colors.accentPrimary}` }}>
                                <label className="text-2xs font-medium block" style={{ color: 'var(--color-text-muted)' }}>修改描述（基于当前变体图微调）</label>
                                <textarea
                                  ref={isExpanded ? variantRefinePromptTextareaRef : null}
                                  className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                                  style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                                  rows={2}
                                  value={variantRefinePrompt}
                                  onChange={e => {
                                    setVariantRefinePrompt(e.target.value);
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  onFocus={e => {
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  onBlur={e => {
                                    const textarea = e.target;
                                    textarea.style.height = 'auto';
                                    textarea.style.height = textarea.scrollHeight + 'px';
                                  }}
                                  placeholder="例如：换成夜晚版本、加上雨天效果..."
                                />
                                <div className="flex justify-end gap-2">
                                  <button onClick={() => { setShowVariantRefinePanel(false); setVariantRefinePrompt(""); }} className="px-3 py-1.5 rounded-md text-xs" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>取消</button>
                                  <button
                                    onClick={() => handleRefineVariant(vi)}
                                    disabled={isRegenerating || !variantRefinePrompt.trim()}
                                    className="workshop-secondary-action px-3 py-1.5 rounded-md text-xs flex items-center gap-1.5 disabled:opacity-50"
                                    style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}
                                  >
                                    {isRegenerating ? <Loader2 size={11} className="animate-spin" /> : <Wand2 size={11} />}
                                    {isRegenerating ? "微调中..." : "开始微调"}
                                  </button>
                                </div>
                              </div>
                            )}

                            {v.imageUrl ? (
                              <div className="workshop-preview relative group aspect-[21/9] rounded-lg overflow-hidden cursor-zoom-in" aria-busy={isRegenerating} style={{ backgroundColor: 'var(--color-bg-overlay)' }} onClick={() => setPreviewImage(v.imageUrl)}>
                                <img src={v.imageUrl} className="w-full h-full object-contain" alt={v.title} />
                                {isRegenerating && (
                                  <div className="workshop-preview-status" role="status">
                                    <span className="workshop-preview-status-dot" aria-hidden="true" />
                                    <span>生成中 · 显示上一版</span>
                                  </div>
                                )}
                                  <>
                                    {/* 下载按钮 */}
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const link = document.createElement('a');
                                        link.href = v.imageUrl;
                                        link.download = `${selectedScene?.name || '场景'}-${v.title || '变体'}.png`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      }}
                                      className="workshop-preview-action absolute top-2 right-2 z-10 w-7 h-7 rounded-full flex items-center justify-center"
                                      style={{ backgroundColor: 'var(--overlay-medium)' }}
                                      title="下载图片"
                                    >
                                      <Download size={14} className="text-white" />
                                    </button>
                                  </>
                              </div>
                            ) : (
                              <div className="aspect-[21/9] rounded-lg border-2 border-dashed flex items-center justify-center" style={{ borderColor: 'var(--color-border-primary)' }}>
                                <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无图片</p>
                              </div>
                            )}

                            <div>
                              <div className="flex items-center justify-between mb-1">
                                <label className="text-2xs font-medium" style={{ color: 'var(--color-text-muted)' }}>变体描述</label>
                                {!editingVariantDesc && (
                                  <button onClick={() => { setEditingVariantDesc(true); setEditVariantDescText(v.description || ""); }} className="workshop-secondary-action workshop-link-action text-2xs flex items-center gap-1" style={{ color: theme.colors.accentPrimary }}>
                                    <Edit2 size={9} /> 编辑
                                  </button>
                                )}
                              </div>
                              {editingVariantDesc ? (
                                <div className="space-y-2">
                                  <textarea
                                    ref={isExpanded ? editVariantDescTextareaRef : null}
                                    rows={2}
                                    className="w-full rounded-md px-3 py-2 text-xs outline-none resize-none"
                                    style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${theme.colors.accentPrimary}`, color: 'var(--color-text-primary)', lineHeight: '1.5' }}
                                    value={editVariantDescText}
                                    onChange={e => {
                                      setEditVariantDescText(e.target.value);
                                      const textarea = e.target;
                                      textarea.style.height = 'auto';
                                      textarea.style.height = textarea.scrollHeight + 'px';
                                    }}
                                    onFocus={e => {
                                      const textarea = e.target;
                                      textarea.style.height = 'auto';
                                      textarea.style.height = textarea.scrollHeight + 'px';
                                    }}
                                    onBlur={e => {
                                      const textarea = e.target;
                                      textarea.style.height = 'auto';
                                      textarea.style.height = textarea.scrollHeight + 'px';
                                    }}
                                    placeholder="描述这个变体的时间/天气/季节..."
                                  />
                                  <div className="flex justify-end gap-1.5">
                                    <button onClick={() => handleSaveVariantDescription(vi)} className="workshop-secondary-action p-1.5 rounded-md" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}><Check size={12} /></button>
                                    <button onClick={() => setEditingVariantDesc(false)} className="p-1.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}><X size={12} /></button>
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs p-2 rounded-md leading-relaxed" style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}>
                                  {v.description || "无描述"}
                                </div>
                              )}
                            </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {sceneVariations.length === 0 && (
                    <div className="workshop-variant-empty flex flex-col items-center justify-center py-12">
                      <Sparkles size={24} className="mb-2 opacity-20" style={{ color: 'var(--color-text-muted)' }} />
                      <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>暂无变体，点击"生成变体"开始（白天/黑夜/春夏秋冬等）</p>
                    </div>
                  )}
                </div>
              </div>

            </div>
          ) : (
            <div className="workshop-empty flex flex-col items-center justify-center h-full">
              <WorkshopArtwork kind="scene" size={48} className="mb-3" />
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>请从左侧选择场景</p>
            </div>
          )}
        </div>
      </div>

      {/* 新建场景弹窗 */}
      {isCreating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'var(--overlay-medium)', backdropFilter: 'blur(8px)' }}>
          <div className="rounded-xl p-8 max-w-5xl w-full max-h-[90vh] overflow-y-auto animate-in fade-in zoom-in-95 duration-200" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold" style={{ color: 'var(--color-text-primary)' }}>新建场景</h3>
              <button onClick={() => { setIsCreating(false); setNewSceneName(""); setNewScenePrompt(""); setNewSceneAliases(""); setNewSceneMode("single"); setReferenceImages([null, null, null, null, null]); }} className="p-1 rounded-lg" style={{ color: 'var(--color-text-muted)' }}><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {/* 场景模式选择 */}
              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>场景模式</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => { setNewSceneMode("single"); }}
                    className="workshop-secondary-action flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 workshop-control-feedback"
                    data-active={newSceneMode === "single"} style={newSceneMode === "single"
                      ? { backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `2px solid ${theme.colors.accentPrimary}` }
                      : { backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-muted)', border: '2px solid var(--color-border-primary)' }}
                  >
                    <Sparkles size={13} /> 单一场景
                  </button>
                  <button
                    onClick={() => { setNewSceneMode("multiAngle"); }}
                    className="workshop-secondary-action flex-1 py-2 rounded-lg text-xs font-bold flex items-center justify-center gap-1.5 workshop-control-feedback"
                    data-active={newSceneMode === "multiAngle"}
                    style={newSceneMode === "multiAngle"
                      ? { backgroundColor: `${theme.colors.accentPrimary}20`, color: theme.colors.accentPrimary, border: `2px solid ${theme.colors.accentPrimary}` }
                      : { backgroundColor: 'var(--color-bg-primary)', color: 'var(--color-text-muted)', border: '2px solid var(--color-border-primary)' }}
                  >
                    <LayoutGrid size={13} /> 16宫格多角度
                  </button>
                </div>
                {newSceneMode === "multiAngle" && (
                  <p className="text-2xs mt-1.5" style={{ color: 'var(--color-text-muted)' }}>
                    一次生成同一场景的16个不同机位，保存后可裁切为16个变体
                  </p>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>画风</label>
                  <AppSelect value={currentStyle} onChange={(e) => setCurrentStyle(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}>
                    {STYLE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                  </AppSelect>
                </div>
                <div>
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>场景名称</label>
                  <input value={newSceneName} onChange={(e) => setNewSceneName(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="例如：教室" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>别名（逗号分隔）</label>
                <input value={newSceneAliases} onChange={(e) => setNewSceneAliases(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="班级, 课室, 教学楼" />
              </div>
              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>场景描述（用于 AI 生成）</label>
                <textarea rows={8} value={newScenePrompt} onChange={(e) => setNewScenePrompt(e.target.value)} className="w-full rounded-lg px-3 py-2 text-xs outline-none resize-none" style={{ backgroundColor: 'var(--color-bg-primary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }} placeholder="描述场景环境、光线、氛围...&#10;&#10;可引用参考图，例如：&#10;参考图1的建筑风格，参考图2的光照" />
              </div>

              {/* 参考图上传区 */}
              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: 'var(--color-text-muted)' }}>
                  参考图（可选，最多{maxReferenceImages}张 — 在描述中写"参考图1的建筑风格"等来引用）
                </label>
                <div className="flex flex-wrap gap-2">
                  {referenceImages.map((img, idx) => {
                    if (!img) return null;
                    const displayIdx = referenceImages.slice(0, idx + 1).filter(Boolean).length;
                    return (
                      <div
                        key={idx}
                        className="relative w-20 h-20 rounded-lg overflow-hidden cursor-zoom-in group"
                        style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)' }}
                        onClick={() => setPreviewImage(img)}
                      >
                        <img src={img} className="w-full h-full object-cover" alt={`参考图${displayIdx}`} />
                        <button
                          onClick={(e) => { e.stopPropagation(); handleRemoveRefImage(idx); }}
                          className="absolute top-1 right-1 p-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          style={{ backgroundColor: 'var(--overlay-medium)', color: 'white' }}
                        >
                          <X size={10} />
                        </button>
                        <div className="absolute bottom-0 left-0 right-0 text-center text-2xs py-0.5 font-bold" style={{ background: 'var(--overlay-medium)', color: 'white' }}>
                          图{displayIdx}
                        </div>
                      </div>
                    );
                  })}
                  {referenceImages.filter(Boolean).length < maxReferenceImages && (
                    <>
                      <input
                        type="file"
                        className="hidden"
                        accept="image/*"
                        ref={el => refImageInputRefs.current[0] = el}
                        onChange={(e) => {
                          const firstEmpty = referenceImages.findIndex(img => !img);
                          if (firstEmpty !== -1) handleUploadRefImage(e, firstEmpty);
                        }}
                      />
                      <button
                        onClick={() => refImageInputRefs.current[0]?.click()}
                        disabled={uploadingRefIndex !== null}
                        className="w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 workshop-control-feedback hover:border-solid disabled:opacity-50"
                        style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-muted)' }}
                      >
                        {uploadingRefIndex !== null ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <>
                            <Plus size={20} />
                            <span className="text-2xs">添加</span>
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* 提示区 */}
              <div className="aspect-[21/9] rounded-lg flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--color-bg-overlay)', border: '1px solid var(--color-border-primary)' }}>
                <div className="flex flex-col items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
                  {newSceneMode === "multiAngle" ? <LayoutGrid size={28} style={{ opacity: 0.5 }} /> : <Sparkles size={28} style={{ opacity: 0.5 }} />}
                  <span className="text-xs">{newSceneMode === "multiAngle" ? "点击「AI 生成16宫格」将在后台异步创建场景" : "点击「AI 生成」将在后台异步创建场景"}</span>
                  <span className="text-2xs opacity-70">或选择「上传图片」立即创建</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleUploadNew} />
                <button onClick={handleGenerateNewMain} disabled={isGenerating} className="workshop-primary-action py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 text-xs disabled:opacity-50" style={newSceneMode === "multiAngle" ? { backgroundColor: theme.colors.accentPrimary, color: 'white' } : { backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                  {isGenerating ? <Loader2 size={13} className="animate-spin" /> : newSceneMode === "multiAngle" ? <LayoutGrid size={13} /> : <Wand2 size={13} />}
                  {isGenerating ? "创建中..." : newSceneMode === "multiAngle" ? "AI 生成16宫格" : "AI 生成"}
                </button>
                <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 text-xs" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-primary)', border: '1px solid var(--color-border-primary)' }}>
                  {isUploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                  {isUploading ? "上传中..." : "上传图片"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AssetRenameDialog
        isOpen={!!renameTarget}
        assetLabel="场景"
        currentName={renameTarget?.name}
        saving={renamingId === renameTarget?.id}
        onClose={() => setRenameTarget(null)}
        onConfirm={handleRename}
      />

      {/* 删除场景确认 */}
      <ConfirmDialog
        isOpen={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={handleDelete}
        title="移入回收站"
        message="确定将这个场景资产移入回收站吗？移入后将不再显示，可联系管理员恢复。"
        confirmLabel="移入回收站"
        variant="danger"
      />

      {/* 删除变体确认 */}
      <ConfirmDialog
        isOpen={deleteVariantIndex !== null}
        onClose={() => setDeleteVariantIndex(null)}
        onConfirm={() => handleDeleteVariant(deleteVariantIndex)}
        title={sceneVariations[deleteVariantIndex]?.imageUrl ? '移入回收站' : '移除变体草稿'}
        message={sceneVariations[deleteVariantIndex]?.imageUrl ? '确定将这个场景变体移入回收站吗？可联系管理员恢复。' : '这个变体尚未生成图片，移除后不会进入媒体回收站。确定移除草稿吗？'}
        confirmLabel={sceneVariations[deleteVariantIndex]?.imageUrl ? '移入回收站' : '移除草稿'}
        variant="danger"
      />
    </div>
  );
};
export default React.memo(SceneGenerator);
